
import { Label } from "@/components/ui/label";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import { useTheme } from "@/hooks/use-theme";

interface GooglePlacesInputProps {
  apiKey: string;
  label?: string;
  placeholder: string;
  onSelect: (place: any) => void;
}

export const GooglePlacesInput = ({ 
  apiKey, 
  placeholder,
  onSelect 
}: GooglePlacesInputProps) => {
  const { theme } = useTheme();
  
  // Theme-aware styles for React Select component
  const getSelectStyles = () => {
    const isDark = theme === 'dark';
    
    return {
      control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: isDark ? 'hsl(240 10% 3.9%)' : 'hsl(0 0% 100%)',
        borderColor: isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)',
        color: isDark ? 'hsl(0 0% 98%)' : 'hsl(240 10% 3.9%)',
        border: `1px solid ${isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)'}`,
        borderRadius: '0.75rem',
        minHeight: '40px',
        width: '100%',
        minWidth: '0',
        boxShadow: 'none',
        outline: 'none',
        '&:hover': {
          borderColor: isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)',
          boxShadow: 'none',
        },
        '&:focus': {
          borderColor: isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)',
          outline: 'none',
          boxShadow: 'none',
        },
        '&:focus-within': {
          borderColor: isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)',
          outline: 'none',
          boxShadow: 'none',
        },
      }),
      valueContainer: (provided: any) => ({
        ...provided,
        padding: '0 12px',
        color: isDark ? 'hsl(0 0% 98%)' : 'hsl(240 10% 3.9%)',
      }),
      input: (provided: any) => ({
        ...provided,
        color: isDark ? 'hsl(0 0% 98%)' : 'hsl(240 10% 3.9%)',
        fontSize: '0.875rem',
        outline: 'none',
        boxShadow: 'none',
      }),
      placeholder: (provided: any) => ({
        ...provided,
        color: isDark ? 'hsl(240 5% 64.9%)' : 'hsl(240 3.8% 46.1%)',
        fontSize: '0.875rem',
      }),
      menu: (provided: any) => ({
        ...provided,
        backgroundColor: isDark ? 'hsl(240 10% 8%)' : 'hsl(0 0% 100%)',
        border: `1px solid ${isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)'}`,
        borderRadius: '0.75rem',
        zIndex: 9999,
        boxShadow: isDark 
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.1)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      }),
      option: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isSelected 
          ? 'var(--primary)'
          : state.isFocused 
            ? isDark ? 'hsl(240 3.7% 15%)' : 'hsl(240 4.8% 95.9%)'
            : 'transparent',
        color: state.isSelected 
          ? '#ffffff'
          : isDark ? 'hsl(0 0% 98%)' : 'hsl(240 10% 3.9%)',
        fontSize: '0.875rem',
        padding: '8px 12px',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: state.isSelected 
            ? 'var(--primary)'
            : isDark ? 'hsl(240 3.7% 15%)' : 'hsl(240 4.8% 95.9%)',
        },
      }),
      indicatorSeparator: (provided: any) => ({
        ...provided,
        backgroundColor: isDark ? 'hsl(240 3.7% 20%)' : 'hsl(240 5.9% 90%)',
      }),
      dropdownIndicator: (provided: any) => ({
        ...provided,
        color: isDark ? 'hsl(240 5% 64.9%)' : 'hsl(240 3.8% 46.1%)',
        '&:hover': {
          color: isDark ? 'hsl(0 0% 98%)' : 'hsl(240 10% 3.9%)',
        },
      }),
    };
  };

  return (
    <div className="w-full">
      <GooglePlacesAutocomplete
        apiKey={apiKey}
        selectProps={{
          value: null,
          onChange: onSelect,
          placeholder,
          className: "react-select-container w-full",
          classNamePrefix: "react-select",
          styles: getSelectStyles(),
          menuPosition: 'absolute',
          menuPlacement: 'bottom',
          menuPortalTarget: null,
        }}
        autocompletionRequest={{
          componentRestrictions: {
            country: ['za']
          }
        }}
      />
    </div>
  );
};
